"use client";

import { useState } from 'react';

type Language = 'en' | 'pt';

interface ContentSection {
  nav: { about: string; expertise: string; lab: string; projects: string; contact: string };
  brand: string;
  heroTitle: string;
  heroText: string;
  aboutTitle: string;
  aboutText: string[];
  expertiseTitle: string;
  expertise: [string, string][];
  technologiesTitle: string;
  technologies: string[];
  timelineTitle: string;
  timeline: { year: string; title: string; desc: string }[];
  labTitle: string;
  labDesc: string;
  projectsTitle: string;
  projects: { title: string; desc: string }[];
  contactTitle: string;
  contact: string;
  contactText: string;
}

export default function WebsiteStarter() {
  const [language, setLanguage] = useState<Language>('en');

  const content: Record<Language, ContentSection> = {
    en: {
      nav: { about: 'About', expertise: 'Expertise', lab: 'Home Lab', projects: 'Projects', contact: 'Contact' },
      brand: 'Infrastructure • Cloud • Security • Automation',
      heroTitle: 'Building Reliable Infrastructure, Securing Systems & Continuously Learning',
      heroText: "I'm Matheus Camargo, an IT professional focused on Microsoft 365, cloud identity, networking, virtualization, storage, automation, and cybersecurity.",
      aboutTitle: 'About Me',
      aboutText: [
        "Hi, I'm Matheus Camargo, an IT Professional and technology enthusiast with a strong passion for infrastructure, cybersecurity, cloud services, networking, virtualization, and automation.",
        "Throughout my career, I have worked with Microsoft 365, Google Workspace, enterprise networking, virtualization platforms, storage systems, cloud identity solutions, and cybersecurity initiatives. I enjoy designing secure, reliable, and scalable environments that help organizations operate efficiently while maintaining strong security standards.",
        "My professional interests include Microsoft 365 administration, cloud identity management, network architecture, virtualization with Proxmox and Hyper-V, enterprise storage using TrueNAS, automation through scripting, monitoring solutions, and cybersecurity best practices. I am particularly interested in infrastructure modernization, security improvement projects, and helping organizations adopt technology in a practical and sustainable way.",
        "Outside of work, I operate a home lab where I test new technologies, self-hosted applications, networking solutions, automation tools, and monitoring platforms. This environment allows me to continuously learn, evaluate new solutions, and experiment with technologies before deploying them in production environments.",
        "Before entering the Information Technology field, I completed a Bachelor's Degree in Biology, which helped develop my analytical thinking, curiosity, research skills, and problem-solving approach.",
        "Beyond technology, I enjoy photography, videography, cycling, music, gaming, and exploring new technologies. These interests contribute to both my creativity and my ability to approach technical challenges from different perspectives.",
        "This website documents my ongoing journey through IT infrastructure, cybersecurity, cloud technologies, networking, automation, self-hosting, and continuous learning."
      ],
      expertiseTitle: 'Areas of Expertise',
      expertise: [
        ['Microsoft 365 & Entra ID', 'Identity management, licensing, cloud services, administration, and security.'],
        ['Infrastructure & Virtualization', 'Windows Server, Hyper-V, Proxmox, Active Directory, and hybrid environments.'],
        ['Networking & Security', 'DNS, VPNs, remote access, monitoring, hardening, and cybersecurity best practices.'],
        ['Storage & Backup', 'TrueNAS, backups, file services, storage management, and disaster recovery.']
      ],
      technologiesTitle: 'Core Technologies',
      technologies: ['Microsoft 365', 'Entra ID', 'Windows Server', 'Hyper-V', 'Proxmox', 'TrueNAS', 'Google Workspace', 'PowerShell/Bash', 'Network Architecture'],
      timelineTitle: 'Professional Journey',
      timeline: [
        { year: 'Present', title: 'IT Professional', desc: 'Focusing on cloud identity, infrastructure modernization, and security.' },
        { year: 'Previous', title: 'Infrastructure & Support', desc: 'Managing corporate networks, virtualization platforms, and storage systems.' },
        { year: 'Education', title: "Bachelor's Degree in Biology", desc: 'Developed strong analytical thinking, research methodologies, and problem-solving skills.' }
      ],
      labTitle: 'Home Lab Environment',
      labDesc: 'A dedicated environment for continuous learning, testing new deployments, and running self-hosted services.',
      projectsTitle: 'Featured Projects',
      projects: [
        { title: 'Infrastructure Modernization', desc: 'Upgrading legacy systems to highly available virtualized environments using Proxmox and Hyper-V.' },
        { title: 'Cloud Identity Rollout', desc: 'Implementing secure access and identity management utilizing Microsoft Entra ID.' },
        { title: 'Enterprise Storage Solution', desc: 'Deploying and managing TrueNAS for robust file services and automated backups.' }
      ],
      contactTitle: "Let's Connect",
      contact: 'Connect on LinkedIn',
      contactText: 'Interested in collaborating or discussing infrastructure and cybersecurity? Reach out to me.'
    },
    pt: {
      nav: { about: 'Sobre', expertise: 'Especialidades', lab: 'Laboratório', projects: 'Projetos', contact: 'Contato' },
      brand: 'Infraestrutura • Nuvem • Segurança • Automação',
      heroTitle: 'Construindo Infraestruturas Confiáveis, Protegendo Sistemas e Aprendendo Continuamente',
      heroText: 'Sou Matheus Camargo, profissional de TI com foco em Microsoft 365, identidade em nuvem, redes, virtualização, armazenamento, automação e cibersegurança.',
      aboutTitle: 'Sobre Mim',
      aboutText: [
        "Olá, eu sou Matheus Camargo, profissional de TI e entusiasta de tecnologia com grande paixão por infraestrutura, cibersegurança, serviços em nuvem, redes, virtualização e automação.",
        "Ao longo da minha carreira, trabalhei com Microsoft 365, Google Workspace, redes corporativas, plataformas de virtualização, sistemas de armazenamento, soluções de identidade em nuvem e iniciativas de cibersegurança. Gosto de projetar ambientes seguros, confiáveis e escaláveis que ajudam organizações a operar com eficiência, mantendo elevados padrões de segurança.",
        "Meus interesses profissionais incluem administração do Microsoft 365, gerenciamento de identidades em nuvem, arquitetura de redes, virtualização com Proxmox e Hyper-V, armazenamento corporativo utilizando TrueNAS, automação por meio de scripts, soluções de monitoramento e boas práticas de cibersegurança. Tenho especial interesse em projetos de modernização de infraestrutura, aprimoramento de segurança e adoção sustentável de tecnologias.",
        "Fora do ambiente de trabalho, mantenho um laboratório doméstico onde avalio novas tecnologias, aplicações self-hosted, soluções de rede, ferramentas de automação e plataformas de monitoramento. Esse ambiente me permite aprender continuamente, testar novas soluções e validar tecnologias antes de implementá-las em ambientes de produção.",
        "Antes de ingressar na área de Tecnologia da Informação, concluí o Bacharelado em Biologia, experiência que contribuiu para o desenvolvimento do meu pensamento analítico, curiosidade, habilidades de pesquisa e abordagem focada na resolução de problemas.",
        "Além da tecnologia, gosto de fotografia, videografia, ciclismo, música, jogos e de explorar novas tecnologias. Esses interesses contribuem tanto para minha criatividade quanto para minha capacidade de abordar desafios técnicos sob diferentes perspectivas.",
        "Este site documenta minha jornada contínua em infraestrutura de TI, cibersegurança, computação em nuvem, redes, automação, self-hosting e aprendizado contínuo."
      ],
      expertiseTitle: 'Áreas de Especialização',
      expertise: [
        ['Microsoft 365 e Entra ID', 'Gerenciamento de identidade, licenciamento, serviços em nuvem, administração e segurança.'],
        ['Infraestrutura e Virtualização', 'Windows Server, Hyper-V, Proxmox, Active Directory e ambientes híbridos.'],
        ['Redes e Segurança', 'DNS, VPNs, acesso remoto, monitoramento, hardening e boas práticas de segurança.'],
        ['Armazenamento e Backup', 'TrueNAS, backups, serviços de arquivos, gerenciamento de armazenamento e recuperação de desastres.']
      ],
      technologiesTitle: 'Principais Tecnologias',
      technologies: ['Microsoft 365', 'Entra ID', 'Windows Server', 'Hyper-V', 'Proxmox', 'TrueNAS', 'Google Workspace', 'PowerShell/Bash', 'Arquitetura de Redes'],
      timelineTitle: 'Jornada Profissional',
      timeline: [
        { year: 'Presente', title: 'Profissional de TI', desc: 'Foco em identidade em nuvem, modernização de infraestrutura e segurança.' },
        { year: 'Anterior', title: 'Infraestrutura e Suporte', desc: 'Gerenciamento de redes corporativas, plataformas de virtualização e armazenamento.' },
        { year: 'Educação', title: 'Bacharelado em Biologia', desc: 'Desenvolvimento de pensamento analítico, metodologias de pesquisa e resolução de problemas.' }
      ],
      labTitle: 'Laboratório Doméstico (Home Lab)',
      labDesc: 'Um ambiente dedicado ao aprendizado contínuo, testes de novas implementações e execução de serviços self-hosted.',
      projectsTitle: 'Projetos em Destaque',
      projects: [
        { title: 'Modernização de Infraestrutura', desc: 'Atualização de sistemas legados para ambientes virtualizados de alta disponibilidade usando Proxmox e Hyper-V.' },
        { title: 'Implementação de Identidade em Nuvem', desc: 'Implementação de acesso seguro e gerenciamento de identidade utilizando Microsoft Entra ID.' },
        { title: 'Solução de Armazenamento Corporativo', desc: 'Implantação e gerenciamento do TrueNAS para serviços de arquivos robustos e backups automatizados.' }
      ],
      contactTitle: 'Vamos nos Conectar',
      contact: 'Conectar no LinkedIn',
      contactText: 'Interessado em colaborar ou discutir sobre infraestrutura e cibersegurança? Entre em contato comigo.'
    }
  };

  const t = content[language];

  return (
    <div className='min-h-screen bg-[#0d1117] text-slate-200 font-sans selection:bg-blue-500/30'>
      
      {/* HEADER */}
      <header className='sticky top-0 z-50 bg-[#0d1117]/80 backdrop-blur-md border-b border-white/10'>
        <div className='max-w-6xl mx-auto px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4'>
          <div className='text-center md:text-left'>
            <h1 className='text-2xl font-extrabold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent'>
              Jigcore
            </h1>
            <p className='text-xs text-slate-400 font-medium tracking-wider uppercase mt-1'>{t.brand}</p>
          </div>

          <div className='flex items-center gap-6'>
            <nav className='hidden md:flex gap-6 text-sm font-medium text-slate-300'>
              <a href="#about" className="hover:text-cyan-400 transition-colors">{t.nav.about}</a>
              <a href="#expertise" className="hover:text-cyan-400 transition-colors">{t.nav.expertise}</a>
              <a href="#lab" className="hover:text-cyan-400 transition-colors">{t.nav.lab}</a>
              <a href="#projects" className="hover:text-cyan-400 transition-colors">{t.nav.projects}</a>
            </nav>
            <div className='flex items-center gap-2 bg-slate-800/50 p-1 rounded-lg border border-white/5 relative z-[60]'>
              <button 
                onClick={() => setLanguage('en')} 
                className={`cursor-pointer px-3 py-1 text-xs font-bold rounded-md transition-all ${language === 'en' ? 'bg-blue-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>
                EN
              </button>
              <button 
                onClick={() => setLanguage('pt')} 
                className={`cursor-pointer px-3 py-1 text-xs font-bold rounded-md transition-all ${language === 'pt' ? 'bg-blue-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>
                PT
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className='max-w-6xl mx-auto px-6 py-12 md:py-20'>
        
        {/* HERO SECTION */}
        <section className='mb-24 text-center md:text-left flex flex-col md:flex-row items-center gap-12'>
          <div className="flex-1 space-y-6">
            <h2 className='text-4xl md:text-6xl font-extrabold leading-tight tracking-tight text-white'>
              {t.heroTitle.split(',').map((part: string, i: number, arr: string[]) => (
                 <span key={i}>
                   {part}{i !== arr.length - 1 ? ',' : ''}
                   {i === 0 && <br className="hidden md:block"/>}
                 </span>
              ))}
            </h2>
            <p className='text-xl text-slate-400 max-w-2xl leading-relaxed'>
              {t.heroText}
            </p>
            <div className="pt-4">
              <a href="#contact" className="inline-block bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-8 rounded-full transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
                {t.contact}
              </a>
            </div>
          </div>
        </section>

        {/* ABOUT SECTION */}
        <section id="about" className='mb-24 scroll-mt-24'>
          <div className='bg-slate-900/40 backdrop-blur-xl border border-white/5 p-8 md:p-12 rounded-3xl relative overflow-hidden'>
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <h3 className='text-3xl font-bold mb-8 flex items-center gap-3 text-white'>
              <span className="w-8 h-1 bg-cyan-400 rounded-full block"></span>
              {t.aboutTitle}
            </h3>
            <div className='space-y-4 text-slate-300 leading-relaxed text-lg relative z-10'>
              {t.aboutText.map((paragraph: string, idx: number) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>

        {/* TIMELINE & TECHNOLOGIES */}
        <section className='mb-24 grid md:grid-cols-2 gap-12'>
          <div>
            <h3 className='text-2xl font-bold mb-8 text-white'>{t.timelineTitle}</h3>
            <div className="space-y-8 border-l-2 border-slate-800 ml-3 pl-8 relative">
              {t.timeline.map((item: { year: string; title: string; desc: string }, idx: number) => (
                <div key={idx} className="relative">
                  <div className="absolute -left-[41px] bg-blue-500 w-4 h-4 rounded-full border-4 border-[#0d1117]"></div>
                  <span className="text-cyan-400 text-sm font-bold tracking-wider uppercase mb-1 block">{item.year}</span>
                  <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-slate-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className='text-2xl font-bold mb-8 text-white'>{t.technologiesTitle}</h3>
            <div className="flex flex-wrap gap-3">
              {t.technologies.map((tech: string) => (
                <span key={tech} className="bg-blue-900/30 border border-blue-500/30 text-blue-300 px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm hover:bg-blue-800/40 transition-colors cursor-default">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* EXPERTISE SECTION */}
        <section id="expertise" className='mb-24 scroll-mt-24'>
          <h3 className='text-3xl font-bold mb-10 text-center text-white'>{t.expertiseTitle}</h3>
          <div className='grid md:grid-cols-2 gap-6'>
            {t.expertise.map(([title, description]: [string, string]) => (
              <div key={title} className='group bg-slate-800/20 hover:bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 hover:border-cyan-500/50 p-8 rounded-2xl transition-all duration-300 relative overflow-hidden'>
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <h4 className='text-xl font-bold mb-3 text-white'>{title}</h4>
                <p className='text-slate-400'>{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* HOME LAB & PROJECTS */}
        <section id="lab" className='mb-24 grid md:grid-cols-2 gap-6 scroll-mt-24'>
          <div className='bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 p-8 rounded-3xl'>
            <h3 className='text-2xl font-bold mb-4 text-cyan-400'>{t.labTitle}</h3>
            <p className='text-slate-300 mb-6'>{t.labDesc}</p>
            <div className="w-full h-32 bg-slate-950/50 rounded-xl border border-slate-800 flex items-center justify-center font-mono text-slate-500 text-sm">
               &gt;_ server status: online
            </div>
          </div>

          <div id="projects" className='bg-slate-900/40 border border-slate-800 p-8 rounded-3xl scroll-mt-24'>
            <h3 className='text-2xl font-bold mb-6 text-white'>{t.projectsTitle}</h3>
            <div className="space-y-4">
              {t.projects.map((proj: { title: string; desc: string }, idx: number) => (
                <div key={idx} className="border-b border-slate-800 pb-4 last:border-0 last:pb-0">
                  <h4 className="font-semibold text-blue-300 mb-1">{proj.title}</h4>
                  <p className="text-sm text-slate-400">{proj.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer id="contact" className='bg-slate-900/50 border-t border-white/5 py-12'>
        <div className='max-w-6xl mx-auto px-6 text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-6'>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">{t.contactTitle}</h2>
            <p className="text-slate-400 max-w-md">{t.contactText}</p>
          </div>
          <a 
            href='https://linkedin.com/in/mhsateu' 
            target='_blank' 
            rel='noopener noreferrer' 
            className='inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white py-3 px-6 rounded-xl transition-all font-medium'
          >
            <svg className="w-5 h-5 text-cyan-400" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            {t.contact}
          </a>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-12 pt-6 border-t border-slate-800 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} Matheus Camargo.
        </div>
      </footer>
    </div>
  );
}
